import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Stack,
  TextField,
  Button,
  Box,
  Alert,
  Divider,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { saveAs } from 'file-saver';
import { usePdf } from '../../context/PdfContext';
import {
  mergePdfs,
  extractPages,
  reorderPages,
  getPdfPageCount,
} from '../../utils/pdfManipulation';

const today = () => new Date().toISOString().slice(0, 10);
const baseName = (name: string) => name.replace(/\.pdf$/i, '');

const PdfToolsPanel: React.FC = () => {
  const { files, currentFileId } = usePdf();

  const currentFile = files.find((f) => f.id === currentFileId);
  const currentIsPdf = currentFile?.fileType === 'pdf';
  const selectedPdfs = files.filter((f) => f.selected && f.fileType === 'pdf');

  const [pageCount, setPageCount] = useState<number | null>(null);
  const [extractRange, setExtractRange] = useState('');
  const [reorderOrder, setReorderOrder] = useState('');
  const [busy, setBusy] = useState<null | 'merge' | 'extract' | 'reorder'>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Load the page count whenever the current PDF changes.
  useEffect(() => {
    let cancelled = false;
    setPageCount(null);
    if (currentIsPdf && currentFile) {
      getPdfPageCount(currentFile.file)
        .then((count) => { if (!cancelled) setPageCount(count); })
        .catch(() => { if (!cancelled) setPageCount(null); });
    }
    return () => { cancelled = true; };
  }, [currentIsPdf, currentFile]);

  // Only show the panel when there is at least one PDF to work with.
  const hasAnyPdf = files.some((f) => f.fileType === 'pdf');
  if (!hasAnyPdf) return null;

  const run = async (
    op: 'merge' | 'extract' | 'reorder',
    action: () => Promise<{ blob: Blob; filename: string }>
  ) => {
    setError(null);
    setSuccess(null);
    setBusy(op);
    try {
      const { blob, filename } = await action();
      saveAs(blob, filename);
      setSuccess(`Saved ${filename}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Operation failed.');
    } finally {
      setBusy(null);
    }
  };

  const handleMerge = () =>
    run('merge', async () => ({
      blob: await mergePdfs(selectedPdfs.map((f) => f.file)),
      filename: `merged-${today()}.pdf`,
    }));

  const handleExtract = () =>
    run('extract', async () => {
      if (!currentFile) throw new Error('Select a PDF first.');
      return {
        blob: await extractPages(currentFile.file, extractRange),
        filename: `${baseName(currentFile.file.name)}-pages.pdf`,
      };
    });

  const handleReorder = () =>
    run('reorder', async () => {
      if (!currentFile) throw new Error('Select a PDF first.');
      return {
        blob: await reorderPages(currentFile.file, reorderOrder),
        filename: `${baseName(currentFile.file.name)}-reordered.pdf`,
      };
    });

  return (
    <Accordion disableGutters sx={{ mt: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
          🛠️ PDF Tools — Merge, Split &amp; Reorder
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          {success && <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>}

          {/* Merge */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>Merge selected PDFs</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Combines all checkbox-selected PDF files into one, in list order.
            </Typography>
            <Tooltip title={selectedPdfs.length < 2 ? 'Select at least two PDF files in the list' : ''}>
              <span>
                <Button
                  variant="outlined"
                  startIcon={busy === 'merge' ? <CircularProgress size={16} /> : <CallMergeIcon />}
                  onClick={handleMerge}
                  disabled={selectedPdfs.length < 2 || busy !== null}
                >
                  Merge {selectedPdfs.length >= 2 ? `${selectedPdfs.length} PDFs` : 'PDFs'}
                </Button>
              </span>
            </Tooltip>
          </Box>

          <Divider />

          {/* Split / Extract */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Extract / split pages{currentIsPdf && pageCount ? ` (current PDF: ${pageCount} pages)` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Build a new PDF from selected pages of the current file. Example: 1-3, 5, 8-10
            </Typography>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                size="small"
                placeholder="e.g. 1-3, 5"
                value={extractRange}
                onChange={(e) => setExtractRange(e.target.value)}
                disabled={!currentIsPdf || busy !== null}
                sx={{ flex: 1 }}
              />
              <Button
                variant="outlined"
                startIcon={busy === 'extract' ? <CircularProgress size={16} /> : <ContentCutIcon />}
                onClick={handleExtract}
                disabled={!currentIsPdf || !extractRange.trim() || busy !== null}
              >
                Extract
              </Button>
            </Stack>
          </Box>

          <Divider />

          {/* Reorder */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>Reorder pages</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              List every page once in the new order. Example for a 3-page PDF: 3, 1, 2
            </Typography>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                size="small"
                placeholder="e.g. 3, 1, 2"
                value={reorderOrder}
                onChange={(e) => setReorderOrder(e.target.value)}
                disabled={!currentIsPdf || busy !== null}
                sx={{ flex: 1 }}
              />
              <Button
                variant="outlined"
                startIcon={busy === 'reorder' ? <CircularProgress size={16} /> : <SwapVertIcon />}
                onClick={handleReorder}
                disabled={!currentIsPdf || !reorderOrder.trim() || busy !== null}
              >
                Reorder
              </Button>
            </Stack>
          </Box>

          {!currentIsPdf && (
            <Typography variant="caption" color="text.secondary">
              Select a PDF file to use extract and reorder.
            </Typography>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default PdfToolsPanel;
